import type { Pinia } from 'pinia';
import type { App } from 'vue';
import type { Router, RouteRecordRaw } from 'vue-router';
import { useTokenStore } from '@common/stores/TokenStore';

export * from './useIsGranted';
export * from './useRouteGranted';

/**
 * Contrôle d'accès côté front.
 *
 * On ne teste **jamais** un rôle, uniquement un droit : le rôle n'est qu'une
 * étiquette que la matrice en dur (`@taxi/shared`) traduit en droits, et l'API
 * embarque ces droits dans le jeton.
 *
 * Ce contrôle est **ergonomique**, pas sécuritaire : il masque ce qui serait de
 * toute façon refusé. L'API revérifie systématiquement, un front modifié ne
 * gagne donc aucun accès.
 */
export default {
	install(app: App, { pinia, router }: { pinia: Pinia; router: Router }) {
		const isGranted = (rights: string | string[]): boolean => {
			const token = useTokenStore(pinia).token;
			if (!token) {
				return false;
			}
			const required = typeof rights === 'string' ? [rights] : rights;
			return required.every((right) => token.rights.includes(right as never));
		};

		/**
		 * Vrai si la route nommée est accessible.
		 *
		 * Les droits d'une route parente s'ajoutent à ceux de ses enfants : une
		 * section protégée protège tout son sous-arbre sans redéclaration.
		 */
		const routeGranted = (routeName: string): boolean => {
			type GrantedRoute = RouteRecordRaw & { meta?: { granted?: string[] } };

			const search = (name: string, routes: readonly RouteRecordRaw[]): GrantedRoute | null => {
				for (const route of routes) {
					if (route.name === name) {
						return route as GrantedRoute;
					}
					if (route.children) {
						const found = search(name, route.children);
						if (found) {
							// Les droits du parent s'ajoutent à ceux de l'enfant : une
							// section protégée protège tout son sous-arbre sans que
							// chaque route ait à redéclarer les mêmes droits.
							return {
								...found,
								meta: {
									...found.meta,
									granted: [
										...((route.meta as GrantedRoute['meta'])?.granted ?? []),
										...(found.meta?.granted ?? []),
									],
								},
							};
						}
					}
				}
				return null;
			};

			const route = search(routeName, router.options.routes);
			return !route || !route.meta?.granted?.length || isGranted(route.meta.granted);
		};

		app.config.globalProperties.$isGranted = isGranted;
		app.config.globalProperties.$routeGranted = routeGranted;
		app.provide('granted', { isGranted, routeGranted });

		// Garde de navigation : une route dont les droits ne sont pas couverts
		// renvoie à l'accueil plutôt que d'afficher un écran vide et des erreurs 403.
		router.beforeEach((to) => {
			const tokenStore = useTokenStore(pinia);
			const granted = (to.meta?.granted ?? []) as string[];

			if (to.meta?.publicPage) {
				return true;
			}
			if (!tokenStore.isAuthenticated) {
				return { name: 'login', query: { redirect: to.fullPath } };
			}
			if (granted.length && !isGranted(granted)) {
				console.warn('Accès refusé à la route', to.name);
				return { name: 'home' };
			}
			return true;
		});
	},
};

declare module 'vue' {
	export interface ComponentCustomProperties {
		$isGranted: (rights: string | string[]) => boolean;
		$routeGranted: (routeName: string) => boolean;
	}
}
